import React, { useEffect, useMemo, useState } from 'react';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import { Paper, Grid, Typography, Button } from '@material-ui/core';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import AutoCompleteInput from '../AutoCompleteInput';
import { i18n } from '../../language';
import logoImg from '../../static/images/web_login_logo.png';
import { userRoleList } from '../../utils/userRole';
import { hospitalWardListFunc } from '../../utils/hospitalWardList';

const LoginBtn = withStyles((theme) => ({
  root: {
    height: '3rem',
    borderRadius: '0.625rem',
    boxShadow: '0',
    textTransform: 'none',
    fontSize: '1rem',
    backgroundColor: '#4c93ff',
    borderColor: '#4c93ff',
    color: '#fff',
    fontFamily: 'NotoSans-Medium',
    '&:hover': {
      backgroundColor: '#538FFF',
      borderColor: '#538FFF',
      boxShadow: 'none',
    },
  },
}))(Button);

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: '2.5rem',
  },
  paper: {
    width: '33.938rem',
    height: '26.938rem',
    position: 'relative',
    padding: '2.625rem 2.5rem 1.938rem',
    borderRadius: '0.937rem',
    boxShadow: '0 -6.3px 12px 0 rgba(0, 0, 0, 0.04)',
  },
  titleText: {
    color: '#001d63',
    fontWeight: 'bold',
    fontSize: '1.875rem',
    fontFamily: 'NotoSans-Bold',
    marginBottom: '0.983rem',
  },
  hintText: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: theme.palette.font.main,
    marginBottom: '2.938rem',
  },
  oval: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '5rem',
    height: '5rem',
    borderRadius: '50% !important',
    boxShadow: '0 5px 10px 0 rgba(0, 0, 0, 0.04)',
    position: 'absolute',
    top: '0',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  },
  logo: {
    fontFamily: 'NotoSans-Bold',
    color: '#001d63',
    fontSize: '0.875rem',
    fontWeight: 'bold',
  },
  grid: {
    height: '100%',
    justifyContent: 'space-between',
  },
  empty: {
    flexGrow: 1,
  },
}));

export default function SetUp({ submitClick }) {
  const classes = useStyles();
  const loginInfo = useSelector((state) => state.loginInfo);
  const userRole = useSelector((state) => state.userRole);

  const hospitalWardList = useMemo(() => {
    return userRoleList.hospWardListDrapformat(userRoleList.filterInActive(userRole));
  }, [userRole]);

  const [HandWOT, setHandWOT] = useState({
    ho: {
      value: loginInfo.hospitalId,
    },
    ward: {
      value: loginInfo.wardId,
    },
  });

  const OtMapping = React.useMemo(() => {
    let HOList = [];
    let WardList = [];
    if (Array.isArray(hospitalWardList) && hospitalWardList.length > 0) {
      HOList = _.map(hospitalWardList, (item) => {
        return { ...item, value: item.hospitalId, text: item.hospitalName };
      });
    }

    const hoIdx = HandWOT.ho.value;
    if (hoIdx > -1) {
      const wards = hospitalWardListFunc.getHospitalAllWardUseId(hospitalWardList, hoIdx);
      if (Array.isArray(wards)) {
        WardList = _.sortBy(
          _.map(wards, (item) => {
            return { ...item, value: item.wardId, text: item.wardName };
          }),
          ['text']
        );
      }
    }
    return {
      HOList,
      WardList,
    };
  }, [hospitalWardList, HandWOT.ho.value]);

  const submitHandle = () => {
    if (HandWOT.ho.value > -1 && HandWOT.ward.value > -1) {
      const hospObj = _.find(hospitalWardList, (item) => {
        return item.hospitalId === HandWOT.ho.value;
      });
      const wardObj = _.find(hospObj?.wards, (item) => {
        return item.wardId === HandWOT.ward.value;
      });
      hospObj &&
        wardObj &&
        submitClick({
          hospitalId: hospObj.hospitalId,
          hospitalName: hospObj.hospitalName,
          wardId: wardObj.wardId,
          wardName: wardObj.wardName,
          hospCode: hospObj.hospCode,
        });
    }
  };

  const keydownEvent = (e) => {
    const EnterCode = 13;
    if (EnterCode === e.keyCode) {
      submitHandle();
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', keydownEvent);
    return () => {
      document.removeEventListener('keydown', keydownEvent);
    };
  }, [HandWOT]);

  return (
    <div className={classes.root}>
      <Paper elevation={3} className={classes.paper}>
        <Grid container direction="column" className={classes.grid}>
          <Typography className={classes.titleText} gutterBottom variant="h5">
            {i18n.t('SETUP_TITLE')}
          </Typography>
          <Typography className={classes.hintText}>{i18n.t('SETUP_SUBTITLE')}</Typography>
          <AutoCompleteInput
            id="setUp_hosp_input"
            itemList={OtMapping.HOList}
            placeholder={i18n.t('SETUP_HOSPITAL_PLACEHOLDER')}
            style={{ marginBottom: '0.625rem' }}
            value={HandWOT.ho.value}
            onChange={(value) => {
              setHandWOT({
                ...HandWOT,
                ho: {
                  value,
                },
                ward: {
                  value: -1,
                },
              });
            }}
          />
          <AutoCompleteInput
            id="setUp_ward_input"
            itemList={OtMapping.WardList}
            placeholder={i18n.t('SETUP_WARD_PLACEHOLDER')}
            value={HandWOT.ward.value}
            onChange={(value) => {
              setHandWOT({
                ...HandWOT,
                ward: {
                  value,
                },
              });
            }}
          />
          <div className={classes.empty} />
          <LoginBtn id="setUp_btn" onClick={submitHandle}>
            {i18n.t('SETUP_TITLE')}
          </LoginBtn>
        </Grid>
        <Paper elevation={3}>
          <img src={logoImg} alt="" className={classes.oval} />
        </Paper>
      </Paper>
    </div>
  );
}
